<template>
  <Transition name="fade">
    <div
      v-show="show ?? false"
      :class="['app-tooltip', $attrs.class, classes()]"
    >
      <p class="app-tooltip__text"><slot></slot></p>
    </div>
  </Transition>
</template>

<script setup lang="ts">
defineOptions({
  inheritAttrs: false,
})

/**
 * A basic tooltip component.
 *
 * Besides the component props, the tooltip can be further customized by overwriting the
 * `--tooltip-width` and `--tooltip-distance` CSS variables.
 *
 * `--tooltip-width` sets the width of the tooltip.
 * `--tooltip-distance` sets the distance between the tooltip and its parent.
 *
 * @param position - Whether to display the tooltip above or below it's parent.
 * @param showArrow - Whether or not to display the arrow pointing to the parent.
 * @param show - Whether to show the tooltip or not.
 */

const props = withDefaults(
  defineProps<{
    position?: 'top' | 'bottom'
    showArrow?: boolean
    show?: boolean
  }>(),
  {
    show: false,
  },
)

function classes(): Record<string, boolean> {
  return {
    'app-tooltip--top': props.position === 'top',
    'app-tooltip--bottom': props.position === 'bottom',
    'app-tooltip--arrow': props.showArrow,
  }
}
</script>

<style lang="scss" scoped>
.app-tooltip {
  --tooltip-width: 390px;
  --tooltip-distance: 12px;
  position: absolute;
  z-index: 3;
  left: calc(50% - (var(--tooltip-width) / 2));
  width: var(--tooltip-width);
  border-radius: 8px;
  border: 1px solid $c-border;
  padding: 16px 24px;
  background: $c-white;
  box-shadow: $shadow;

  &__text {
    color: $c-black-15;
    text-align: center;
    font-size: 14px;
    line-height: 20px;
    margin-bottom: -3px;
  }
}

.app-tooltip--top {
  bottom: calc(100% + var(--tooltip-distance));
  &.app-tooltip--arrow:before {
    bottom: 0;
    translate: 0 var(--half-tooltip-distance);
    border-top: none;
    border-left: none;
  }
}

.app-tooltip--bottom {
  top: calc(100% + var(--tooltip-distance));

  &.app-tooltip--arrow:before {
    top: 0;
    translate: 0 calc(var(--half-tooltip-distance) * -1);
    border-bottom: none;
    border-right: none;
  }
}

.app-tooltip--arrow:before {
  --half-tooltip-distance: calc(var(--tooltip-distance) / 2);

  content: '';
  position: absolute;
  right: calc(50% - var(--half-tooltip-distance));
  width: var(--tooltip-distance);
  height: var(--tooltip-distance);
  border: 1px solid $c-border;

  background: $c-white;
  rotate: 45deg;
}

.night-mode {
  .app-tooltip,
  .app-tooltip:before {
    background: $c-dark-1;
    border-color: $c-dark-3;
  }

  .app-tooltip__text {
    color: $c-dark-4;
  }
}
</style>
